import React, { useState, useEffect } from 'react';

/**
 * MapGenerationLoader - Displays a loading overlay with progress bar
 * for dot density and heat map generation
 */
const MapGenerationLoader = ({
  isVisible,
  mapType = 'heatmap', // 'heatmap' or 'dotdensity'
  currentStage = 0,
  totalStages = 5,
  areaCount = null
}) => {
  const [displayedProgress, setDisplayedProgress] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);
  const [dots, setDots] = useState('');

  // Fun messages to keep users entertained
  const messages = {
    heatmap: [
      "Warming up the color palette...",
      "Calculating demographic ranges...",
      "Painting the map with data...",
      "Arranging the legend just right...",
      "Adding the finishing touches...",
      "Almost there! Polishing the pixels...",
      "Making it look pretty...",
      "Crunching the numbers...",
      "Consulting the geography gods...",
      "Mixing the perfect color blend..."
    ],
    dotdensity: [
      "Counting all those tiny dots...",
      "Placing dots with precision...",
      "Teaching dots where to live...",
      "Making dots behave themselves...",
      "Scattering dots artistically...",
      "Each dot tells a story...",
      "Dots are social distancing...",
      "Organizing the dot party...",
      "Dots are finding their homes...",
      "Creating dot density magic..."
    ]
  };

  const stageLabels = {
    heatmap: [
      "Initializing...",
      "Querying geographic features...",
      "Enriching with demographic data...",
      "Calculating class breaks...",
      "Rendering visualization...",
      "Finalizing map..."
    ],
    dotdensity: [
      "Initializing...",
      "Querying geographic features...",
      "Enriching with demographic data...",
      "Calculating dot distribution...",
      "Rendering dots...",
      "Finalizing map..."
    ]
  };

  // Animate progress bar smoothly
  useEffect(() => {
    const targetProgress = Math.min((currentStage / totalStages) * 100, 100);

    const interval = setInterval(() => {
      setDisplayedProgress(prev => {
        const diff = targetProgress - prev;
        if (Math.abs(diff) < 0.5) return targetProgress;
        return prev + diff * 0.1;
      });
    }, 50);

    return () => clearInterval(interval);
  }, [currentStage, totalStages]);

  // Cycle through fun messages
  useEffect(() => {
    if (!isVisible) return;

    const messageList = messages[mapType] || messages.heatmap;
    const interval = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % messageList.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isVisible, mapType]);

  // Animate loading dots
  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 400);

    return () => clearInterval(interval);
  }, [isVisible]);

  // Reset on visibility change
  useEffect(() => {
    if (isVisible) {
      setDisplayedProgress(0);
      setMessageIndex(0);
    }
  }, [isVisible]);

  if (!isVisible) return null;

  const messageList = messages[mapType] || messages.heatmap;
  const stages = stageLabels[mapType] || stageLabels.heatmap;
  const currentLabel = stages[Math.min(currentStage, stages.length - 1)] || "Processing...";

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8 max-w-md w-full mx-4 transform transition-all">
        {/* Header */}
        <div className="flex items-center justify-center mb-6">
          <div className="relative">
            {/* Animated spinner */}
            <svg
              className="animate-spin h-12 w-12 text-blue-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="3"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            {/* Map type icon overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              {mapType === 'dotdensity' ? (
                <span className="text-lg">...</span>
              ) : (
                <span className="text-lg text-blue-600 dark:text-blue-400">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm1 0v12h12V4H4z" clipRule="evenodd" />
                  </svg>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Title */}
        <h3 className="text-xl font-semibold text-center text-gray-900 dark:text-white mb-2">
          {mapType === 'dotdensity' ? 'Generating Dot Density Map' : 'Generating Heat Map'}
          <span className="text-blue-500">{dots}</span>
        </h3>

        {/* Current stage label */}
        <p className="text-sm text-center text-gray-600 dark:text-gray-400 mb-4">
          {currentLabel}
        </p>

        {/* Progress bar container */}
        <div className="relative mb-4">
          {/* Background bar */}
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            {/* Animated progress fill */}
            <div
              className="h-full rounded-full transition-all duration-300 ease-out relative overflow-hidden"
              style={{
                width: `${displayedProgress}%`,
                background: mapType === 'dotdensity'
                  ? 'linear-gradient(90deg, #10B981, #3B82F6, #8B5CF6)'
                  : 'linear-gradient(90deg, #3B82F6, #8B5CF6, #EC4899)'
              }}
            >
              {/* Shimmer effect */}
              <div
                className="absolute inset-0 animate-shimmer"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  backgroundSize: '200% 100%'
                }}
              />
            </div>
          </div>

          {/* Progress percentage */}
          <div className="absolute right-0 -top-6 text-sm font-medium text-gray-700 dark:text-gray-300">
            {Math.round(displayedProgress)}%
          </div>
        </div>

        {/* Stage indicators */}
        <div className="flex justify-between mb-6">
          {stages.slice(0, totalStages).map((_, idx) => (
            <div
              key={idx}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                idx <= currentStage
                  ? 'bg-blue-500 scale-125'
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}
            />
          ))}
        </div>

        {/* Fun message */}
        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 mb-4">
          <p className="text-sm text-center text-blue-700 dark:text-blue-300 italic transition-opacity duration-500">
            "{messageList[messageIndex]}"
          </p>
        </div>

        {/* Area count info if available */}
        {areaCount && (
          <p className="text-xs text-center text-gray-500 dark:text-gray-400">
            Processing {areaCount.toLocaleString()} geographic areas
          </p>
        )}

        {/* Warning message */}
        <div className="mt-4 flex items-center justify-center gap-2 text-amber-600 dark:text-amber-400">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span className="text-xs">Please don't close this window</span>
        </div>
      </div>

      {/* Custom animation styles */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite linear;
        }
      `}</style>
    </div>
  );
};

export default MapGenerationLoader;
